/**
 * CSRF 防护：为 POST/PUT/DELETE/PATCH 请求附加 X-Csrf-Token（与后端 CsrfInterceptor 一致）
 * 必须对「已创建好的 axios 实例」调用 setupCsrfInterceptor，禁止在此文件 top-level import api，避免循环依赖。
 *
 * Token 使用独立 fetch 拉取，避免在 axios 请求拦截器内再调 axios.get 导致拦截器链/响应解包异常。
 */
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { logger } from './logger';

let csrfToken: string | null = null;
const CSRF_TOKEN_HEADER = 'X-Csrf-Token';

/** 与后端 CsrfConfig 排除路径一致：这些写操作不需要附带 CSRF */
const CSRF_SKIP_URL_PARTS = [
  '/auth/login',
  '/auth/logout',
  '/csrf/token',
  '/public/',
  'auth/login',
  'auth/logout',
];

export function invalidateCsrfToken(): void {
  csrfToken = null;
}

function resolveRequestUrl(config: InternalAxiosRequestConfig): string {
  const raw = config.url || '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }
  const base = (config.baseURL || '').replace(/\/$/, '');
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${base}${path}`.replace(/\/{2,}/g, '/');
}

function shouldAttachCsrf(config: InternalAxiosRequestConfig | undefined): boolean {
  if (!config) {
    return false;
  }
  if (config.skipCsrf === true) {
    return false;
  }
  const method = config.method?.toUpperCase();
  if (!['POST', 'PUT', 'DELETE', 'PATCH'].includes(method || '')) {
    return false;
  }
  const fullUrl = resolveRequestUrl(config);
  return !CSRF_SKIP_URL_PARTS.some((part) => fullUrl.includes(part));
}

function extractCsrfFromBody(body: unknown): string | null {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const rec = body as { code?: number; data?: unknown };
  if (rec.code !== 200 || rec.data == null) {
    return null;
  }
  if (typeof rec.data === 'string') {
    return rec.data;
  }
  if (typeof rec.data === 'object' && rec.data !== null && 'csrfToken' in rec.data) {
    return (rec.data as { csrfToken?: string }).csrfToken || null;
  }
  return null;
}

function resolveCsrfTokenUrl(axiosInstance: AxiosInstance): string {
  const base = (axiosInstance.defaults.baseURL || '/api').replace(/\/$/, '');
  if (base.startsWith('http://') || base.startsWith('https://')) {
    return `${base}/csrf/token`;
  }
  const path = base.startsWith('/') ? `${base}/csrf/token` : `/${base}/csrf/token`;
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  return path;
}

/** 独立 fetch，不经过 axios 拦截器 */
async function fetchCsrfTokenDirect(axiosInstance: AxiosInstance): Promise<string> {
  const url = resolveCsrfTokenUrl(axiosInstance);
  const headers: Record<string, string> = { Accept: 'application/json' };
  const satoken =
    typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (satoken) {
    headers.satoken = satoken;
  }

  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
  if (!res.ok) {
    throw new Error(`CSRF HTTP ${res.status}`);
  }
  const body: unknown = await res.json();
  const token = extractCsrfFromBody(body);
  if (!token) {
    throw new Error('CSRF 响应无有效 token');
  }
  csrfToken = token;
  return token;
}

function isCsrfBusinessError(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const rec = data as { code?: number; message?: string };
  return (
    rec.code === 600 &&
    typeof rec.message === 'string' &&
    rec.message.includes('CSRF')
  );
}

export function setupCsrfInterceptor(axiosInstance: AxiosInstance) {
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (!config) {
        return Promise.reject(new Error('axios 请求配置缺失'));
      }
      if (!shouldAttachCsrf(config)) {
        return config;
      }
      if (!csrfToken) {
        try {
          await fetchCsrfTokenDirect(axiosInstance);
        } catch (err) {
          logger.warn('获取CSRF Token失败，写操作可能需重试', err);
        }
      }

      if (csrfToken) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>)[CSRF_TOKEN_HEADER] = csrfToken;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    async (response: AxiosResponse) => {
      const cfg = response.config as (InternalAxiosRequestConfig & { __csrfRetried?: boolean }) | undefined;
      if (!cfg) {
        return response;
      }
      const method = cfg.method?.toUpperCase();
      if (
        ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method || '') &&
        !cfg.__csrfRetried &&
        isCsrfBusinessError(response.data)
      ) {
        try {
          invalidateCsrfToken();
          await fetchCsrfTokenDirect(axiosInstance);
          cfg.__csrfRetried = true;
          if (csrfToken) {
            cfg.headers = cfg.headers ?? {};
            (cfg.headers as Record<string, string>)[CSRF_TOKEN_HEADER] = csrfToken;
          }
          return axiosInstance(cfg);
        } catch (retryError) {
          logger.error('CSRF 业务错误后重试失败:', retryError);
        }
      }
      return response;
    },
    async (error: { response?: { status?: number; data?: { message?: string } }; config?: AxiosRequestConfig }) => {
      if (
        error.response?.status === 403 &&
        typeof error.response?.data?.message === 'string' &&
        error.response.data.message.includes('CSRF')
      ) {
        try {
          invalidateCsrfToken();
          await fetchCsrfTokenDirect(axiosInstance);
          const originalRequest = error.config;
          if (!originalRequest) {
            return Promise.reject(error);
          }
          if (originalRequest.headers && csrfToken) {
            (originalRequest.headers as Record<string, string>)[CSRF_TOKEN_HEADER] = csrfToken;
          }
          return axiosInstance(originalRequest);
        } catch (retryError) {
          logger.error('重试请求失败:', retryError);
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
}

export async function refreshCsrfToken(axiosInstance: AxiosInstance): Promise<void> {
  await fetchCsrfTokenDirect(axiosInstance);
}

export function getCsrfToken(): string | null {
  return csrfToken;
}
